import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { useInfiniteScroll } from '@/components/asset-manager/hooks/useInfiniteScroll';
import type { UnsplashImage } from '@/components/asset-manager/types';
import { useUnsplashStore } from '@/stores/unsplash-store';

interface UseUnsplashImagesArgs {
  defaultQuery: string;
  scrollContainerRef?: RefObject<HTMLDivElement | null>;
  debounceMs?: number;
}

interface UseUnsplashImagesReturn {
  images: UnsplashImage[];
  searchInput: string;
  setSearchInput: (value: string) => void;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  hasNextPage: boolean;
  sentinelRef: RefObject<HTMLDivElement | null>;
  retry: () => void;
}

export function useUnsplashImages({
  defaultQuery,
  scrollContainerRef,
  debounceMs = 500,
}: UseUnsplashImagesArgs): UseUnsplashImagesReturn {
  const { getCached, loadUnsplash } = useUnsplashStore();

  const [images, setImages] = useState<UnsplashImage[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setLocalError] = useState<string | null>(null);

  const defaultQueryRef = useRef(defaultQuery);
  const activeQueryRef = useRef<string>(defaultQueryRef.current);
  const requestIdRef = useRef(0);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Core fetch function. Handles two modes:
   *   - First page: uses cache when available, otherwise shows main loader.
   *     Pass `force: true` (retry) to bypass cache.
   *   - Append (page > 1): triggered by infinite scroll. The "loading more"
   *     state is managed by useInfiniteScroll automatically.
   */
  const fetchImages = useCallback(
    async (query: string, page: number, options: { append?: boolean; force?: boolean } = {}) => {
      const { append = false, force = false } = options;
      const isFirstPage = page === 1 && !append;
      activeQueryRef.current = query;
      const requestId = ++requestIdRef.current;

      if (isFirstPage) {
        const cached = !force ? getCached(query) : undefined;
        if (cached) {
          setImages(cached.images);
          setCurrentPage(cached.currentPage);
          setTotalPages(cached.totalPages);
          setIsLoading(false);
        } else {
          setIsLoading(true);
        }
      }

      try {
        const { results, totalPages: tp } = await loadUnsplash(query, page, {
          append: !isFirstPage,
          force,
        });

        if (activeQueryRef.current !== query || requestIdRef.current !== requestId) return;

        if (isFirstPage) {
          setImages(results);
          setCurrentPage(1);
        } else {
          setImages((prev) => [...prev, ...results]);
          setCurrentPage(page);
        }
        setTotalPages(tp);
      } catch {
        if (isFirstPage && requestIdRef.current === requestId) {
          setImages([]);
          setCurrentPage(1);
          setTotalPages(1);
          setLocalError('Failed to load Unsplash images. Please try again.');
        }
      } finally {
        if (isFirstPage && requestIdRef.current === requestId) {
          setIsLoading(false);
        }
      }
    },
    [getCached, loadUnsplash],
  );

  const didInitialLoadRef = useRef(false);

  useEffect(() => {
    if (didInitialLoadRef.current) return;

    didInitialLoadRef.current = true;

    void fetchImages(defaultQueryRef.current, 1);
  }, [fetchImages]);

  useEffect(() => {
    if (defaultQueryRef.current === defaultQuery) return;

    defaultQueryRef.current = defaultQuery;

    // Only refetch default results when user is not actively searching.
    if (searchInput.trim() === '') {
      setLocalError(null);
      void fetchImages(defaultQuery, 1);
    }
  }, [defaultQuery, searchInput, fetchImages]);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      const trimmed = searchInput.trim();
      const queryToFetch = trimmed === '' ? defaultQueryRef.current : trimmed;

      // Skip refetch if the query hasn't actually changed.
      if (queryToFetch === activeQueryRef.current) return;

      // Clear any stale error from a previous query before firing a new one.
      setLocalError(null);
      void fetchImages(queryToFetch, 1);
    }, debounceMs);

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [searchInput, debounceMs, fetchImages]);

  // Infinite scroll — useInfiniteScroll owns the isLoadingMore state and
  // toggles it around the onLoadMore promise.
  const hasNextPage = currentPage < totalPages;

  const handleLoadMore = useCallback(async () => {
    await fetchImages(activeQueryRef.current, currentPage + 1, { append: true });
  }, [currentPage, fetchImages]);

  const { sentinelRef, isLoadingMore } = useInfiniteScroll({
    hasNextPage,
    isLoading,
    onLoadMore: handleLoadMore,
    rootMargin: '200px',
    root: scrollContainerRef,
  });

  const retry = useCallback(() => {
    setLocalError(null);
    void fetchImages(activeQueryRef.current, 1, { force: true });
  }, [fetchImages]);

  return {
    images,
    searchInput,
    setSearchInput,
    isLoading,
    isLoadingMore,
    error,
    hasNextPage,
    sentinelRef,
    retry,
  };
}
